/**
 * DataPreview Component
 *
 * Preview panel for dataset creation showing selected measurements/fields,
 * a data preview chart, dataset name input, and create button.
 */

import { useState } from "react";
import { Database, Loader2, X, Save, LineChart } from "lucide-react";
import { Button } from "@/shared/ui/button";
import { Input } from "@/shared/ui/input";
import { Label } from "@/shared/ui/label";
import { Badge } from "@/shared/ui/badge";
import { ScrollArea } from "@/shared/ui/scroll-area";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/shared/ui/card";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/shared/ui/collapsible";
import { cn } from "@/shared/lib/utils";
import { DatasetPreviewChart } from "./DatasetPreviewChart";
import type { MeasurementFieldSelections, SelectedDatapoint } from "../../types/dataset";

interface DataPreviewProps {
  bucket: string;
  selections: MeasurementFieldSelections;
  onRemoveField: (measurement: string, field: string) => void;
  onSave: (name: string, limit?: number) => void;
  isSaving: boolean;
  className?: string;
  // Optional props for chart preview
  unsSelections?: Map<string, SelectedDatapoint>;
  rawSelections?: MeasurementFieldSelections;
  dataSourceId?: string;
  startTime?: Date;
  endTime?: Date;
}

export function DataPreview({
  bucket,
  selections,
  onRemoveField,
  onSave,
  isSaving,
  className,
  unsSelections,
  rawSelections,
  dataSourceId,
  startTime,
  endTime,
}: DataPreviewProps) {
  const [datasetName, setDatasetName] = useState("");
  const [rowLimit, setRowLimit] = useState<string>("");
  const [chartOpen, setChartOpen] = useState(true);

  const selectedMeasurements = Object.keys(selections);
  const totalFields = Object.values(selections).reduce((acc, fields) => acc + fields.length, 0);

  const handleCreate = () => {
    if (!datasetName.trim()) return;
    const limit = rowLimit ? parseInt(rowLimit, 10) : undefined;
    onSave(datasetName.trim(), limit);
  };

  const canCreate = datasetName.trim().length > 0 && totalFields > 0;

  if (totalFields === 0) {
    return (
      <div className={cn("flex flex-col items-center justify-center h-full text-muted-foreground p-8", className)}>
        <Database className="h-16 w-16 mb-4 opacity-30" />
        <h3 className="text-lg font-semibold mb-2">No Fields Selected</h3>
        <p className="text-sm text-center max-w-md">
          Select measurements and fields from the browser to preview your dataset configuration.
        </p>
      </div>
    );
  }

  return (
    <div className={cn("flex flex-col min-h-full", className)}>
      {/* Header */}
      <div className="p-4 border-b">
        <h3 className="font-semibold">Dataset Configuration</h3>
        <p className="text-sm text-muted-foreground">
          {totalFields} field{totalFields !== 1 ? "s" : ""} from {selectedMeasurements.length} measurement{selectedMeasurements.length !== 1 ? "s" : ""}
        </p>
      </div>

      {/* Data Preview Chart */}
      {startTime && endTime && (unsSelections || (rawSelections && dataSourceId && bucket)) && (
        <Collapsible open={chartOpen} onOpenChange={setChartOpen} className="border-b">
          <CollapsibleTrigger asChild>
            <button
              type="button"
              className="flex items-center justify-between w-full p-3 hover:bg-muted/50 transition-colors"
            >
              <div className="flex items-center gap-2 text-sm font-medium">
                <LineChart className="h-4 w-4" />
                Data Preview
              </div>
              <Badge variant="outline" className="text-xs">
                {chartOpen ? "Hide" : "Show"}
              </Badge>
            </button>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <div className="h-[180px] px-3 pb-3">
              <DatasetPreviewChart
                unsSelections={unsSelections}
                rawSelections={rawSelections}
                dataSourceId={dataSourceId}
                bucket={bucket}
                startTime={startTime}
                endTime={endTime}
              />
            </div>
          </CollapsibleContent>
        </Collapsible>
      )}

      {/* Selected Fields */}
      <ScrollArea className="flex-1 p-4">
        <div className="space-y-4">
          {selectedMeasurements.map((measurement) => (
            <Card key={measurement} className="bg-muted/30">
              <CardHeader className="py-3 px-4">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Database className="h-4 w-4" />
                  {measurement}
                </CardTitle>
                <CardDescription className="text-xs">
                  {selections[measurement].length} field{selections[measurement].length !== 1 ? "s" : ""} selected
                </CardDescription>
              </CardHeader>
              <CardContent className="py-2 px-4">
                <div className="flex flex-wrap gap-1.5">
                  {selections[measurement].map((field) => (
                    <Badge
                      key={field}
                      variant="secondary"
                      className="text-xs pr-1 gap-1"
                    >
                      {field}
                      <button
                        type="button"
                        onClick={() => onRemoveField(measurement, field)}
                        className="ml-1 hover:bg-destructive/20 rounded p-0.5"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </ScrollArea>

      {/* Create Form */}
      <div className="p-4 border-t space-y-4 bg-muted/30">
        <div className="space-y-2">
          <Label htmlFor="dataset-name">Dataset Name *</Label>
          <Input
            id="dataset-name"
            placeholder="Enter dataset name..."
            value={datasetName}
            onChange={(e) => setDatasetName(e.target.value)}
            disabled={isSaving}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="row-limit">Row Limit (optional)</Label>
          <Input
            id="row-limit"
            type="number"
            placeholder="Leave empty for no limit"
            value={rowLimit}
            onChange={(e) => setRowLimit(e.target.value)}
            disabled={isSaving}
            min={1}
          />
          <p className="text-xs text-muted-foreground">
            Limit the number of rows extracted from InfluxDB
          </p>
        </div>

        <div className="flex items-center justify-between pt-2">
          <div className="text-xs text-muted-foreground">
            Source: <Badge variant="outline" className="ml-1">{bucket}</Badge>
          </div>
          <Button
            onClick={handleCreate}
            disabled={!canCreate || isSaving}
          >
            {isSaving ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Creating...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                Create Dataset
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default DataPreview;
